// CookieNotice.js
import React, { useState, useEffect } from "react";
import styles from "./CookieNotice.module.css";

const CookieNotice = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("necessary");
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Check if user has already made cookie choices
    const cookieChoices = localStorage.getItem("cookiePreferences");
    
    if (!cookieChoices) {
      setIsVisible(true);
    } else {
      try {
        const savedPreferences = JSON.parse(cookieChoices);
        setPreferences(savedPreferences);
      } catch (e) {
        // If there's an error parsing, show the notice again
        setIsVisible(true);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    localStorage.setItem("cookiePreferences", JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem("cookiePreferences", JSON.stringify(preferences));
    setIsVisible(false);
  };

  const handlePreferenceChange = (category) => {
    if (category === "necessary") return; // Cannot toggle necessary cookies
    
    setPreferences({
      ...preferences,
      [category]: !preferences[category]
    });
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  if (!isVisible) return null;

  return (
    <div className={styles.cookieNoticeOverlay}>
      <div className={styles.cookieNotice}>
        <div className={styles.header}>
          <h2>Privacy Preferences</h2>
          <button 
            className={styles.closeButton}
            onClick={() => setIsVisible(false)}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.tabs}>
            <button 
              className={`${styles.tabButton} ${activeTab === "necessary" ? styles.activeTab : ""}`}
              onClick={() => handleTabChange("necessary")}
            >
              Necessary
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === "analytics" ? styles.activeTab : ""}`}
              onClick={() => handleTabChange("analytics")}
            >
              Analytics
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === "marketing" ? styles.activeTab : ""}`}
              onClick={() => handleTabChange("marketing")}
            >
              Marketing
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === "preferences" ? styles.activeTab : ""}`}
              onClick={() => handleTabChange("preferences")}
            >
              Preferences
            </button>
          </div>

          <div className={styles.tabContent}>
            {activeTab === "necessary" && (
              <div>
                <h3>Strictly Necessary Cookies</h3>
                <p>
                  These cookies are essential for enabling user movement around our website and providing access to features such as your profile and purchases, member-only resources, and other secure areas of the website. These cookies do not gather information about you that could be used for marketing purposes or remembering where you've been on the internet. This category of cookies cannot be disabled.
                </p>
                <div className={styles.cookieOption}>
                  <input 
                    type="checkbox" 
                    id="necessary-cookies" 
                    checked={preferences.necessary} 
                    disabled 
                  />
                  <label htmlFor="necessary-cookies">Always Active</label>
                </div>
              </div>
            )}

            {activeTab === "analytics" && (
              <div>
                <h3>Analytics Cookies</h3>
                <p>
                  These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. They allow us to recognize and count the number of visitors and to see how visitors move around our site when they are using it.
                </p>
                <div className={styles.cookieOption}>
                  <input 
                    type="checkbox" 
                    id="analytics-cookies" 
                    checked={preferences.analytics} 
                    onChange={() => handlePreferenceChange("analytics")}
                  />
                  <label htmlFor="analytics-cookies">Enable Analytics Cookies</label>
                </div>
              </div>
            )}

            {activeTab === "marketing" && (
              <div>
                <h3>Marketing Cookies</h3>
                <p>
                  These cookies are used to track visitors across websites. The intention is to display ads that are relevant and engaging for the individual user and thereby more valuable for publishers and third-party advertisers.
                </p>
                <div className={styles.cookieOption}>
                  <input 
                    type="checkbox" 
                    id="marketing-cookies" 
                    checked={preferences.marketing} 
                    onChange={() => handlePreferenceChange("marketing")}
                  />
                  <label htmlFor="marketing-cookies">Enable Marketing Cookies</label>
                </div>
              </div>
            )}

            {activeTab === "preferences" && (
              <div>
                <h3>Preference Cookies</h3>
                <p>
                  These cookies allow our website to remember choices you make and provide enhanced, more personal features. They may also be used to provide services you have asked for such as watching a video or commenting on a blog.
                </p>
                <div className={styles.cookieOption}>
                  <input 
                    type="checkbox" 
                    id="preference-cookies" 
                    checked={preferences.preferences} 
                    onChange={() => handlePreferenceChange("preferences")}
                  />
                  <label htmlFor="preference-cookies">Enable Preference Cookies</label>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className={styles.actions}>
          <button 
            onClick={handleSavePreferences} 
            className={styles.saveButton}
          >
            Save Preferences
          </button>
          <button 
            onClick={handleAcceptAll} 
            className={styles.acceptButton}
          >
            Accept All
          </button>
        </div>

        <div className={styles.footer}>
          <p>For more information about our cookie practices, please review our <a href="/privacy-policy">Privacy Policy</a>.</p>
        </div>
      </div>
    </div>
  );
};

export default CookieNotice;